/*
 * moveTypeLookup.ts — printed move name → Pokémon GO move type.
 *
 * Backing data is the FULL move list already checked in for the raid meta
 * (`raidMeta.generated.ts` RAID_MOVES, from the PokéMiners Game Master —
 * fast and charged moves alike), so the My Pokémon move chips can carry
 * their type's colour without a new network generator. Names are matched
 * on normalized alphanumerics so an OCR'd "Zen Headbutt" and the Game
 * Master's "Zen Headbutt" meet regardless of spacing/punctuation.
 *
 * UNIONED WITH THE PVP TABLE (2026-09-04): the raid generator keeps only
 * moves some species' Game Master pool references, and the Game Master
 * grants Behemoth Bash / Behemoth Blade to Crowned Zamazenta / Zacian
 * outside their pools, so those two real charged moves were unknown here
 * and every scan of a Crowned Zamazenta silently lost Behemoth Bash. The
 * PvP table (`combatMove`, every move) carries them; either table makes a
 * name known.
 */
import { RAID_MOVES } from "./generated/raidMeta.generated";
import { PVP_MOVES } from "./generated/pvpMoves.generated";
import { MEGA_ONLY_MOVES } from "./movePoolAdditions";
import type { PokeType } from "./types";

let cache: Map<string, PokeType> | null = null;

function normalize(name: string): string {
  return (name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function table(): Map<string, PokeType> {
  if (cache) return cache;
  const map = new Map<string, PokeType>();
  for (const m of RAID_MOVES) {
    map.set(normalize(m.name), m.type as PokeType);
  }
  for (const m of PVP_MOVES) {
    const key = normalize(m.name);
    if (!map.has(key)) map.set(key, m.type as PokeType);
  }
  // Mega-only "+" moves (movePoolAdditions, 2026-09-16): no Game Master row
  // yet, but GO prints them on a Mega-Evolved Pokémon's page.
  for (const m of MEGA_ONLY_MOVES) {
    const key = normalize(m.name);
    if (!map.has(key)) map.set(key, m.type as PokeType);
  }
  cache = map;
  return map;
}

/**
 * The comparison key for a move name: lower-case alphanumerics, with a
 * trailing "+" kept because it is the whole difference between a Mega-only
 * move ("Dark Pulse+") and its base ("Dark Pulse"). Every place that asks
 * "is this the same move?" must go through this, not `toLowerCase()`: the
 * game prints "Future Sight" where the Game Master says "Futuresight", and
 * a record holding the printed spelling never matched its own row
 * (reported 2026-09-16: a scanned third move sat unselected on the detail
 * page until it was cleared and re-picked).
 */
export function moveNameKey(name: string): string {
  const trimmed = (name || "").trim();
  return normalize(trimmed) + (trimmed.endsWith("+") ? "+" : "");
}

let nameCache: Map<string, string> | null = null;

/** moveNameKey → the corpus's own spelling, first table wins. */
function nameTable(): Map<string, string> {
  if (nameCache) return nameCache;
  const map = new Map<string, string>();
  for (const m of [...RAID_MOVES, ...PVP_MOVES, ...MEGA_ONLY_MOVES]) {
    const key = moveNameKey(m.name);
    if (!map.has(key)) map.set(key, m.name);
  }
  nameCache = map;
  return map;
}

/**
 * The Game Master's spelling of a printed move name, so a scan stores the
 * same string the move tables use and every later comparison is exact.
 * Exact key first; then a single unambiguous prefix match (the page is
 * clipped mid-word, or prints "Weather Ball" for the typed variants). Null
 * when nothing matches or the prefix fans out to several moves: the caller
 * keeps what it read.
 */
export function canonicalMoveName(name: string): string | null {
  const key = moveNameKey(name);
  const exact = nameTable().get(key);
  if (exact) return exact;
  if (key.length < 4) return null;
  let found: string | null = null;
  for (const [candidate, canonical] of nameTable()) {
    if (!candidate.startsWith(key)) continue;
    if (found != null && found !== canonical) return null;
    found = canonical;
  }
  return found;
}

/**
 * The move's GO type, or null when the name matches nothing we know.
 *
 * Exact match first; then a PREFIX match, because the Game Master stores
 * typed variants under longer names than the game prints — the page says
 * "Weather Ball", the corpus holds "Weather Ball Fire/Ice/Rock/Water". A
 * prefix that fans out to several TYPES returns null (known move, ambiguous
 * type — the chip renders neutral).
 */
export function moveTypeFor(name: string): PokeType | null {
  const key = normalize(name);
  const exact = table().get(key);
  if (exact) return exact;
  if (key.length < 4) return null;
  let found: PokeType | null = null;
  for (const [candidate, type] of table()) {
    if (!candidate.startsWith(key)) continue;
    if (found != null && found !== type) return null; // ambiguous type
    found = type;
  }
  return found;
}

/**
 * Whether this is a real GO move name (exact or a prefix of the corpus's
 * typed variants) — the categorical junk filter for scanned movesets.
 */
export function isKnownMoveName(name: string): boolean {
  const key = normalize(name);
  if (table().has(key)) return true;
  if (key.length < 4) return false;
  for (const candidate of table().keys()) {
    if (candidate.startsWith(key)) return true;
  }
  return false;
}
